<template>
  <div>
    <slot />
    <slot v-if="activeHover" name="hover" :feature="activeHover" />
  </div>
</template>

<script setup lang="ts">
import { GeoJSONFeature, LayerSpecification, MapMouseEvent } from 'mapbox-gl'
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useMap } from '../mapbox/context'
import { useMapHover } from './MapHoverState'

const { map } = useMap()
const { hoveredFeature, cursorPosition, setHoveredFeature } = useMapHover()

const source = inject<string>('source')
const isMapMoving = ref(false)

const props = withDefaults(
  defineProps<{
    layers?: LayerSpecification[]
    filter?: any[]
    hoverCursor?: string
    before?: string
    hideLayer?: boolean
  }>(),
  {
    layers: () => [],
    filter: undefined,
    hoverCursor: 'pointer',
    before: undefined,
  },
)

const emits = defineEmits<{
  (event: 'click', data: GeoJSONFeature[], eventData: MapMouseEvent)
}>()

const ownsFeature = (feature: GeoJSONFeature) =>
  feature.source === source && props.layers.some((layer) => layer.id === feature.layer?.id)

const activeHover = computed(() => {
  const feature = hoveredFeature.value
  return !props.hideLayer && feature && ownsFeature(feature) ? feature : null
})

const setFilters = (filters?: any[]) => {
  props.layers
    .map(({ id }) => id)
    .forEach((layerId) => {
      if (map.value?.getLayer(layerId)) {
        map.value.setFilter(layerId, filters)
      }
    })
}

const setBefore = (before?: any) => {
  props.layers.forEach((layer) => {
    if (map.value?.getLayer(layer.id)) {
      map.value.moveLayer(layer.id, before)
    }
  })
}

const setHidden = (hidden?: boolean) => {
  if (hidden) clearHover()
  props.layers.forEach((layer) => {
    if (map.value?.getLayer(layer.id)) {
      map.value?.setLayoutProperty(layer.id, 'visibility', hidden === true ? 'none' : 'visible')
    }
  })
}

watch(() => props.filter, setFilters)
watch(() => props.before, setBefore)
watch(() => props.hideLayer, setHidden)

onMounted(() => {
  props.layers.forEach((layer) => {
    const beforeLayerExists = props.before && map.value?.getLayer(props.before)
    map.value?.addLayer(
      {
        ...layer,
        ...{ source: source as any },
      },
      beforeLayerExists ? props.before : undefined,
    )
  })

  if (props.filter) {
    setFilters(props.filter)
  }

  if (props.hideLayer) {
    setHidden(props.hideLayer)
  }

  map.value?.on('mousemove', handleMouseMove)
  map.value?.on('mouseout', clearHover)
  map.value?.on('click', handleClick)
  map.value?.on('movestart', handleMoveStart)
  map.value?.on('moveend', handleMoveEnd)
})

onBeforeUnmount(() => {
  clearHover()

  props.layers.forEach((layer) => {
    if (map.value?.getLayer(layer.id)) {
      map.value.removeLayer(layer.id)
    }
  })

  map.value?.off('mousemove', handleMouseMove)
  map.value?.off('mouseout', clearHover)
  map.value?.off('click', handleClick)
  map.value?.off('movestart', handleMoveStart)
  map.value?.off('moveend', handleMoveEnd)
})

// Only hit-test this group's layers that still exist on the map.
const presentLayerIds = () => props.layers.map((l) => l.id).filter((id) => map.value?.getLayer(id))

const isSameFeature = (feature: GeoJSONFeature, previous?: GeoJSONFeature | null) =>
  previous != null &&
  feature.id === previous.id &&
  feature.source === previous.source &&
  feature.sourceLayer === previous.sourceLayer

const handleMouseMove = (e: MapMouseEvent) => {
  if (props.hideLayer || isMapMoving.value) return

  const layerIds = presentLayerIds().filter((id) => {
    const layer = props.layers.find((layer) => layer.id === id)
    return (layer?.metadata as { hover?: boolean } | undefined)?.hover !== false
  })
  if (layerIds.length === 0) {
    // Layers can vanish at runtime (parent swapped its definitions); don't
    // leave the cursor and hover feature-state stuck on a dead hover.
    clearHover()
    return
  }

  const features = map.value?.queryRenderedFeatures(e.point, { layers: layerIds }) ?? []
  const feature = features.find((f) => f.source === source)

  if (!feature || feature.id == null) {
    clearHover()
    return
  }

  cursorPosition.value = feature.geometry.type === 'Point' ? null : [e.lngLat.lng, e.lngLat.lat]

  if (isSameFeature(feature, hoveredFeature.value)) return

  const previous = hoveredFeature.value
  if (previous) {
    map.value?.setFeatureState(previous, { hover: false })
  }

  if (map.value) {
    map.value.getCanvas().style.cursor = props.hoverCursor
    map.value.setFeatureState(feature, { hover: true })
    setHoveredFeature(feature)
  }
}

const handleMoveStart = () => {
  isMapMoving.value = true
  clearHover()
}

const handleMoveEnd = () => {
  isMapMoving.value = false
}

const handleClick = (e: MapMouseEvent) => {
  if (props.hideLayer) return
  const layerIds = presentLayerIds()
  if (layerIds.length === 0) return

  const features = map.value?.queryRenderedFeatures(e.point, { layers: layerIds }) ?? []

  if (features.length) {
    emits('click', features, e)
  }
}

const clearHover = () => {
  const feature = hoveredFeature.value
  if (!feature || !ownsFeature(feature)) return

  map.value?.setFeatureState(feature, { hover: false })
  setHoveredFeature(null)

  if (map.value) {
    map.value.getCanvas().style.cursor = ''
  }
}
</script>
